import React, { useEffect, useState } from "react";
import API from "../services/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  CalendarX, Clock, CheckCircle, AlertCircle, ArrowLeft,
  FileText, Upload, Send, User, Building2,
} from "lucide-react";
import { FORM_GRADE_OPTIONS, SENIOR_SCHOOL_GRADES, PATHWAY_OPTIONS } from "../utils/academicLevels";

const ClosedBanner = ({ message }) => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 gap-5">
    <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center">
      <CalendarX size={36} className="text-red-400" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-2">Applications Closed</h2>
      <p className="text-gray-500 text-sm max-w-sm leading-relaxed">{message}</p>
    </div>
  </div>
);

const CycleBanner = ({ cycle }) => {
  const deadline = new Date(cycle.applicationDeadline);
  const now      = new Date();
  const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  const isUrgent = daysLeft <= 7;

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm mb-6
      ${isUrgent ? "bg-orange-50 border-orange-200 text-orange-800" : "bg-green-50 border-green-200 text-green-800"}`}
    >
      <Clock size={16} className="flex-shrink-0" />
      <div>
        <span className="font-semibold">{cycle.name}</span>
        {" "}Application deadline:{" "}
        <span className="font-semibold">
          {deadline.toLocaleDateString("en-KE", { dateStyle: "long" })}
        </span>
        {isUrgent && (
          <span className="ml-2 font-bold text-orange-600">
            ({daysLeft} {daysLeft === 1 ? "day" : "days"} left!)
          </span>
        )}
      </div>
    </div>
  );
};

const LEVEL_LABELS = {
  SECONDARY: "Secondary School",
  COLLEGE: "College",
  UNIVERSITY: "University",
};

const STEPS = [
  { label: "Progress",  icon: FileText },
  { label: "Documents", icon: Upload },
  { label: "Submit",    icon: Send },
];

const ApplicationForm = () => {
  const navigate = useNavigate();

  const [cycleStatus, setCycleStatus]     = useState("loading");
  const [cycle, setCycle]                 = useState(null);
  const [closedMessage, setClosedMessage] = useState("");
  const [profile, setProfile]             = useState(null);

  const [step, setStep]               = useState(1);
  const [submitting, setSubmitting]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [existingApp, setExistingApp] = useState(null);

  const [progress, setProgress] = useState({
    formGrade: "", pathway: "", stream: "", yearOfStudy: "",
    totalFees: "", feeBalance: "",
  });
  const [files, setFiles] = useState({
    studentIdDoc: null, feeStructure: null,
    guardianIdDoc: null, reportCard: null, admissionLetter: null,
  });

  useEffect(() => {
    const init = async () => {
      const [cycleRes, profileRes] = await Promise.allSettled([
        API.get("/cycles/active"),
        API.get("/student/profile"),
      ]);

      if (profileRes.status === "fulfilled") {
        setProfile(profileRes.value.data);
      }

      if (cycleRes.status === "fulfilled") {
        setCycle(cycleRes.value.data);
        setCycleStatus("open");
      } else if (cycleRes.reason?.response?.status === 404) {
        setCycleStatus("closed");
        setClosedMessage(
          "There is no active bursary application cycle at the moment. " +
          "Please check back later or contact the CDF office for the next cycle dates."
        );
      } else {
        setCycleStatus("error");
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (cycleStatus !== "open" || !cycle) return;
    API.get("/applications/latest")
      .then((res) => {
        if (res.data.academicYear === cycle.academicYear) {
          setExistingApp(res.data);
          const p = res.data.academicProgress || {};
          setProgress({
            formGrade:   p.formGrade   || "",
            pathway:     p.pathway     || "",
            stream:      p.stream      || "",
            yearOfStudy: p.yearOfStudy || "",
            totalFees:   res.data.fees?.totalFees  || "",
            feeBalance:  res.data.fees?.feeBalance || "",
          });
          if (["SUBMITTED", "APPROVED", "REJECTED"].includes(res.data.status)) {
            setStep(3);
          }
        }
      })
      .catch(() => {});
  }, [cycleStatus, cycle]);

  const setField = (name, value) => setProgress((prev) => ({ ...prev, [name]: value }));
  const schoolLevel = profile?.schoolLevel;
  const isSenior = SENIOR_SCHOOL_GRADES.includes(progress.formGrade);

  const step1Valid = (() => {
    if (!schoolLevel || !progress.totalFees) return false;
    if (schoolLevel === "SECONDARY") return Boolean(progress.formGrade);
    return Boolean(progress.yearOfStudy);
  })();

  const handleSaveDraft = async () => {
    if (!step1Valid) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      const payload = {
        academicYear: cycle.academicYear,
        totalFees:    progress.totalFees,
        feeBalance:   progress.feeBalance || 0,
      };
      if (schoolLevel === "SECONDARY") {
        payload.formGrade = progress.formGrade;
        payload.pathway   = progress.pathway;
        payload.stream    = progress.stream;
      } else {
        payload.yearOfStudy = progress.yearOfStudy;
      }

      await API.post("/applications/draft", payload);
      toast.success("Details saved, proceed to upload documents");
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save details");
    }
  };

  const handleUpload = async () => {
    if (!files.studentIdDoc || !files.feeStructure) {
      toast.error("ID document and fee structure are required");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("academicYear", cycle.academicYear);
      Object.entries(files).forEach(([key, file]) => {
        if (file) formData.append(key, file);
      });
      await API.post("/applications/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documents uploaded, ready to submit");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm("Submit your bursary application? You cannot edit it after submission.")) return;
    setSubmitting(true);
    try {
      await API.post("/applications/submit", { academicYear: cycle.academicYear });
      toast.success("Application submitted successfully! You will be notified by email.");
      navigate("/student-dashboard/status");
    } catch (err) {
      toast.error(err.response?.data?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors bg-white";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

  const FileField = ({ name, label, required }) => (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {!required && <span className="text-xs text-gray-400 ml-1 font-normal">(optional)</span>}
      </label>
      <label className={`flex items-center gap-3 border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer transition-colors
        ${files[name] ? "border-green-400 bg-green-50" : "border-gray-200 hover:border-primary bg-white"}`}
      >
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="sr-only"
          onChange={(e) => setFiles((prev) => ({ ...prev, [name]: e.target.files[0] }))}
        />
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
          ${files[name] ? "bg-green-100" : "bg-gray-100"}`}
        >
          {files[name]
            ? <CheckCircle size={16} className="text-green-600" />
            : <Upload size={16} className="text-gray-400" />
          }
        </div>
        <div className="min-w-0">
          {files[name]
            ? <p className="text-sm font-medium text-green-700 truncate">{files[name].name}</p>
            : <><p className="text-sm text-gray-500">Click to upload</p><p className="text-xs text-gray-400">PDF, JPG, PNG, max 5MB</p></>
          }
        </div>
      </label>
    </div>
  );

  if (cycleStatus === "loading") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-sm">Checking application status...</p>
      </div>
    );
  }

  if (cycleStatus === "closed") return <ClosedBanner message={closedMessage} />;

  if (cycleStatus === "error") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-4">
        <AlertCircle size={36} className="text-red-400" />
        <p className="text-gray-600">Could not connect to the server. Please try again.</p>
        <button onClick={() => window.location.reload()} className="bg-primary text-white px-5 py-2 rounded-xl text-sm">
          Retry
        </button>
      </div>
    );
  }

  const alreadySubmitted = existingApp && ["SUBMITTED", "APPROVED", "REJECTED"].includes(existingApp.status);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <button onClick={() => navigate("/student-dashboard")} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 mb-5 transition">
        <ArrowLeft size={14} /> Back to Dashboard
      </button>

      <h2 className="text-2xl font-bold text-primary mb-1">Bursary Application</h2>
      <p className="text-gray-500 text-sm mb-5">Complete all steps to submit your application for this academic year.</p>

      {/* Read only identity, pulled from the profile, not re-entered here */}
      {profile && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <User size={16} className="text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-800">{profile.fullName}</p>
            <p className="text-xs text-gray-500">{LEVEL_LABELS[profile.schoolLevel] || profile.schoolLevel}</p>
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-gray-500">
              <Building2 size={12} className="flex-shrink-0" />
              <span className="truncate">
                {profile.institution?.institutionName}
                {profile.institution?.admissionNumber && ` · Admission No. ${profile.institution.admissionNumber}`}
                {profile.institution?.registrationNumber && ` · Reg. No. ${profile.institution.registrationNumber}`}
              </span>
            </div>
          </div>
          <button
            onClick={() => navigate("/student-dashboard/profile")}
            className="text-xs font-medium text-primary hover:underline flex-shrink-0"
          >
            Edit in Profile
          </button>
        </div>
      )}

      {cycle && <CycleBanner cycle={cycle} />}

      {alreadySubmitted && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4 mb-5 text-sm text-blue-800">
          <CheckCircle size={16} className="flex-shrink-0 text-blue-500" />
          <div>
            Your application has been <strong>{existingApp.status}</strong>.{" "}
            <button onClick={() => navigate("/student-dashboard/status")} className="underline font-medium">
              View status →
            </button>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6">
        {STEPS.map((s, i) => {
          const n = i + 1;
          const isCompleted = step > n;
          const isCurrent   = step === n;
          return (
            <div key={s.label} className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-xl border text-xs font-medium transition
              ${isCompleted ? "bg-green-50 border-green-200 text-green-700"
                : isCurrent ? "bg-primary border-primary text-white"
                : "bg-gray-50 border-gray-200 text-gray-400"}`}
            >
              <s.icon size={14} />
              {n}. {s.label}
            </div>
          );
        })}
      </div>

      <div className="flex gap-1 mb-6">
        {[1, 2, 3].map((n) => (
          <div key={n} className={`flex-1 h-1.5 rounded-full transition-colors duration-300 ${step >= n ? "bg-primary" : "bg-gray-200"}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-gray-800 mb-0.5">This Year's Progress</h3>
            <p className="text-xs text-gray-400">Only what has changed since last time. Your institution details are already on file.</p>
          </div>

          {schoolLevel === "SECONDARY" && (
            <>
              <div>
                <label className={labelClass}>Grade / Form <span className="text-red-500">*</span></label>
                <select value={progress.formGrade} onChange={(e) => setField("formGrade", e.target.value)} className={inputClass}>
                  <option value="">Select Grade / Form</option>
                  {FORM_GRADE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {isSenior && (
                <div>
                  <label className={labelClass}>Senior School Pathway <span className="text-xs text-gray-400 ml-1 font-normal">(optional)</span></label>
                  <select value={progress.pathway} onChange={(e) => setField("pathway", e.target.value)} className={inputClass}>
                    <option value="">Select Pathway</option>
                    {PATHWAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className={labelClass}>Class / Stream <span className="text-xs text-gray-400 ml-1 font-normal">(optional)</span></label>
                <input value={progress.stream} onChange={(e) => setField("stream", e.target.value)} placeholder="e.g. 10 East" className={inputClass} />
              </div>
            </>
          )}

          {(schoolLevel === "COLLEGE" || schoolLevel === "UNIVERSITY") && (
            <div>
              <label className={labelClass}>Year of Study <span className="text-red-500">*</span></label>
              <input type="number" min={1} value={progress.yearOfStudy} onChange={(e) => setField("yearOfStudy", e.target.value)} placeholder="e.g. 2" className={inputClass} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Total Fees (KES) <span className="text-red-500">*</span></label>
              <input type="number" value={progress.totalFees} onChange={(e) => setField("totalFees", e.target.value)} placeholder="e.g. 45000" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Fee Balance (KES)</label>
              <input type="number" value={progress.feeBalance} onChange={(e) => setField("feeBalance", e.target.value)} placeholder="e.g. 30000" className={inputClass} />
            </div>
          </div>

          <button
            onClick={handleSaveDraft}
            disabled={!step1Valid}
            className={`w-full py-2.5 rounded-xl text-sm font-semibold transition mt-2 ${
              step1Valid ? "bg-primary text-white hover:bg-blue-800 active:scale-95" : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            Save & Continue →
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-gray-800 mb-0.5">Upload Documents</h3>
            <p className="text-xs text-gray-400">Upload clear scans or photos. Max 5MB per file.</p>
          </div>
          <FileField name="studentIdDoc"    label="ID / Birth Certificate" required />
          <FileField name="feeStructure"    label="Fee Structure / Invoice" required />
          <FileField name="guardianIdDoc"   label="Guardian ID" />
          <FileField name="admissionLetter" label="Admission Letter" />
          <FileField name="reportCard"      label="Latest Report Card" />
          <div className="flex gap-3 mt-2">
            <button onClick={() => setStep(1)} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition">
              ← Back
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1 bg-primary text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 active:scale-95 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              Upload & Continue →
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5 text-center">
          <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 text-lg mb-1">Ready to Submit</h3>
            <p className="text-gray-500 text-sm">
              Your details and documents are saved. Click below to submit your application for{" "}
              <strong>{cycle?.academicYear}</strong>.
            </p>
          </div>
          {alreadySubmitted ? (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800">
              Your application is already <strong>{existingApp.status}</strong>.{" "}
              <button onClick={() => navigate("/student-dashboard/status")} className="underline font-medium">
                View status →
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition">
                ← Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-green-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-green-700 active:scale-95 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Submit Application
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicationForm;